import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, Send, Paperclip, Search, RefreshCw, Check, CheckCheck, AlertCircle, ExternalLink, FileText, X, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import WhatsAppAutoReplyModal from "./WhatsAppAutoReplyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TICKS = {
  sent: { Icon: Check, color: "text-zinc-500", label: "Trimis" },
  delivered: { Icon: CheckCheck, color: "text-zinc-500", label: "Livrat" },
  read: { Icon: CheckCheck, color: "text-sky-400", label: "Citit" },
  failed: { Icon: AlertCircle, color: "text-rose-400", label: "Eșuat" },
};

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Small colored pill showing which platform an order belongs to.
 *  eMAG = red, Gomag = amber, other = zinc. Uses the plaintext platform
 *  label instead of a raster logo so it stays crisp at 10px. */
function PlatformBadge({ platform }) {
  const p = (platform || "").toLowerCase();
  const map = {
    emag: { label: "eMAG", cls: "border-rose-500/40 bg-rose-500/15 text-rose-300" },
    gomag: { label: "Bragus", cls: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  };
  const cfg = map[p] || { label: p || "?", cls: "border-zinc-600/40 bg-zinc-700/40 text-zinc-300" };
  return (
    <span
      data-testid={`wa-platform-badge-${p}`}
      className={`inline-flex items-center rounded-sm border px-1 py-[1px] text-[9px] font-bold uppercase leading-none ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function MessageBubble({ msg }) {
  const isOut = msg.direction === "outbound";
  const tick = isOut ? (TICKS[msg.status] || TICKS.sent) : null;
  const TickIcon = tick?.Icon;

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${msg.wa_id || msg.id}`}>
      <div className={`max-w-[68%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
        isOut ? "bg-emerald-500/15 border border-emerald-500/30 text-zinc-100"
              : "bg-zinc-900 border border-zinc-800 text-zinc-100"
      }`}>
        {msg.type === "text" && (
          <div className="whitespace-pre-wrap break-words">{msg.content}</div>
        )}
        {msg.type === "template" && (
          <div>
            <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/25 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-200">
              <FileText className="h-2.5 w-2.5" />
              Template{msg.template_name ? ` · ${msg.template_name}` : ""}
            </div>
            <div className="whitespace-pre-wrap break-words">{msg.content || msg.body_text || msg.template_name}</div>
          </div>
        )}
        {!["text", "template", "image", "document", "audio", "video"].includes(msg.type) && (msg.content || msg.body_text) && (
          <div className="whitespace-pre-wrap break-words">{msg.content || msg.body_text}</div>
        )}
        {msg.type === "image" && msg.media_id && (
          <a href={`${API}/whatsapp/media/${msg.media_id}`} target="_blank" rel="noreferrer">
            <img alt={msg.caption || "imagine"} src={`${API}/whatsapp/media/${msg.media_id}`}
                 className="max-h-56 rounded-lg object-cover" />
          </a>
        )}
        {(msg.type === "document" || msg.type === "audio" || msg.type === "video") && (
          <a href={`${API}/whatsapp/media/${msg.media_id}`} target="_blank" rel="noreferrer"
             className="flex items-center gap-2 rounded-md bg-black/20 px-2.5 py-1.5 hover:bg-black/40">
            <Paperclip className="h-3.5 w-3.5" />
            <span className="truncate font-mono text-xs">{msg.filename || `${msg.type}.${(msg.mime_type || "").split("/").pop()}`}</span>
            <ExternalLink className="h-3 w-3 shrink-0 text-zinc-400" />
          </a>
        )}
        {msg.caption && msg.type !== "text" && (
          <div className="mt-1.5 text-xs text-zinc-300">{msg.caption}</div>
        )}
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-zinc-500">
          <span>{fmtTime(msg.timestamp)}</span>
          {isOut && TickIcon && (
            <TickIcon className={`h-3 w-3 ${tick.color}`} aria-label={tick.label} />
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatePicker({ open, onClose, onSend, sending }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/whatsapp/templates`);
      setTemplates((r.data?.items || []).filter((t) => t.status === "APPROVED"));
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { load(); setSelected(null); setParams([]); } }, [open, load]);

  const handleSelect = (tpl) => {
    setSelected(tpl);
    setParams(Array((tpl.placeholder_count || 0)).fill(""));
  };

  const preview = useMemo(() => {
    if (!selected) return "";
    let text = selected.body_text || "";
    params.forEach((p, i) => {
      text = text.replaceAll(`{{${i + 1}}}`, p || `{{${i + 1}}}`);
    });
    return text;
  }, [selected, params]);

  const canSend = selected && params.every((p) => p.trim().length > 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="wa-template-picker">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <FileText className="h-4 w-4 text-emerald-400" />
            Trimite template WhatsApp
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100" data-testid="wa-template-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left: template list */}
          <div className="max-h-[520px] overflow-y-auto border-r border-zinc-900">
            {loading ? (
              <div className="p-6 text-center text-xs text-zinc-500">Se încarcă template-urile...</div>
            ) : templates.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-500">
                Niciun template APPROVED încă. Creează unul în Meta Business Manager și așteaptă aprobarea.
              </div>
            ) : (
              templates.map((t) => (
                <button
                  key={t.id || t.name}
                  type="button"
                  onClick={() => handleSelect(t)}
                  data-testid={`wa-template-${t.name}`}
                  className={`flex w-full flex-col items-start gap-1 border-b border-zinc-900 px-3 py-2.5 text-left transition ${
                    selected?.name === t.name ? "bg-emerald-500/10" : "hover:bg-zinc-900/50"
                  }`}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-zinc-100">{t.name}</span>
                    <span className="shrink-0 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-300">
                      {t.category}
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500">
                    {t.language} · {t.placeholder_count} placeholderi
                  </div>
                  <div className="line-clamp-2 text-xs text-zinc-400">{t.body_text}</div>
                </button>
              ))
            )}
          </div>

          {/* Right: preview + params */}
          <div className="max-h-[520px] overflow-y-auto p-4">
            {!selected ? (
              <div className="pt-16 text-center text-xs text-zinc-500">Selectează un template din stânga.</div>
            ) : (
              <>
                <div className="mb-3 text-xs text-zinc-400">
                  <span className="font-medium text-zinc-200">{selected.name}</span>
                  <span className="mx-1">·</span>
                  <span>{selected.language}</span>
                </div>
                {selected.placeholder_count > 0 && (
                  <div className="mb-3 space-y-2">
                    <div className="text-[11px] font-semibold text-zinc-300">Placeholderi:</div>
                    {Array.from({ length: selected.placeholder_count }).map((_, i) => (
                      <div key={i}>
                        <label className="mb-0.5 block text-[10px] text-zinc-500">{`{{${i + 1}}}`}</label>
                        <input
                          type="text"
                          value={params[i] || ""}
                          onChange={(e) => {
                            const next = [...params];
                            next[i] = e.target.value;
                            setParams(next);
                          }}
                          placeholder={`Valoare pentru {{${i + 1}}}`}
                          data-testid={`wa-template-param-${i + 1}`}
                          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-emerald-500"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase text-zinc-500">Preview</div>
                  <div className="whitespace-pre-wrap text-xs text-zinc-200">{preview}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onSend(selected, params)}
                  disabled={!canSend || sending}
                  data-testid="wa-template-send"
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sending ? "Trimit..." : "Trimite template"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversationRow({ conv, active, onClick, onDelete, deleting }) {
  const linked = Array.isArray(conv.linked_orders) ? conv.linked_orders : [];
  const orderLabels = linked.slice(0, 2).map((o) => o.display_id).filter(Boolean);
  const extraCount = Math.max(0, linked.length - orderLabels.length);
  const [showDelete, setShowDelete] = useState(false);
  const pressTimerRef = useRef(null);
  const pressedRef = useRef(false);
  const startPress = () => {
    pressedRef.current = false;
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      pressedRef.current = true;
      setShowDelete(true);
    }, 500);
  };
  const cancelPress = () => clearTimeout(pressTimerRef.current);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (pressedRef.current) { pressedRef.current = false; return; }
        if (showDelete) { setShowDelete(false); return; }
        onClick?.();
      }}
      onMouseDown={startPress}
      onMouseUp={cancelPress}
      onMouseLeave={cancelPress}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
      onTouchCancel={cancelPress}
      onContextMenu={(e) => { e.preventDefault(); setShowDelete(true); }}
      data-testid={`wa-conv-${conv.peer_number}`}
      className={`relative flex w-full cursor-pointer select-none items-center gap-2 border-b border-zinc-900 px-3 py-2.5 text-left transition ${
        active ? "bg-zinc-900/80" : "hover:bg-zinc-900/50"
      } ${showDelete ? "ring-1 ring-rose-500/40" : ""}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
        <MessageCircle className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-zinc-100">
            {conv.contact_name || conv.peer_number}
          </span>
          <span className="shrink-0 text-[10px] text-zinc-500">{fmtTime(conv.last_message_at)}</span>
        </div>
        {orderLabels.length > 0 && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            {orderLabels.map((label) => (
              <span
                key={label}
                data-testid={`wa-conv-linked-order-${label}`}
                className="inline-flex items-center rounded border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] text-emerald-300"
                title="Comandă asociată numărului"
              >
                [{label}]
              </span>
            ))}
            {extraCount > 0 && (
              <span className="text-[10px] text-zinc-500">+{extraCount}</span>
            )}
          </div>
        )}
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-zinc-500">{conv.last_message_preview || "—"}</span>
          {conv.unread_count > 0 && (
            <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {conv.unread_count}
            </span>
          )}
        </div>
      </div>
      {showDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(conv); }}
          disabled={deleting}
          data-testid={`wa-conv-delete-${conv.peer_number}`}
          title="Șterge conversația"
          className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-md border border-rose-500/50 bg-rose-500/90 px-2 py-1 text-xs font-medium text-white shadow-lg hover:bg-rose-500 disabled:opacity-60"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleting ? "Șterg…" : "Șterge"}
        </button>
      )}
    </div>
  );
}

export default function WhatsAppPage() {
  // ─── Persistență localStorage ─────────────────────────────────────
  // Snapshot al conversațiilor + mesajelor afișate ultima dată. La mount
  // le afișăm INSTANT (fără spinner/loading) și le împrospătăm în background.
  // Elimină senzația de "flicker/refresh permanent" din UI.
  const CONVS_CACHE = "bragus_wa_global_convs_v1";
  const MSGS_CACHE_PREFIX = "bragus_wa_global_msgs_v1::";
  const ENRICH_CACHE = "bragus_wa_global_enrich_v1"; // {peer: {linked_orders, linked_returns}}
  const readLS = (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } };
  const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
  const enrichCacheRef = useRef(readLS(ENRICH_CACHE) || {});

  const [conversations, setConversations] = useState(() => {
    const cached = readLS(CONVS_CACHE) || [];
    // Aplică enrichment cached ca să apară imediat linked_orders/linked_returns.
    const ec = enrichCacheRef.current || {};
    return cached.map((c) => ec[c.peer_number] ? { ...c, ...ec[c.peer_number] } : c);
  });
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAutoReply, setShowAutoReply] = useState(false);
  const [sendingFormLink, setSendingFormLink] = useState(false);
  const [deletingPeer, setDeletingPeer] = useState(null);
  const fileInputRef = useRef(null);
  const threadEndRef = useRef(null);

  // Amprentă pentru detectare schimbări — evită re-render inutil.
  const convsFingerprint = (list) =>
    (list || []).map((c) => `${c.peer_number}|${c.last_message_at || ""}|${c.unread_count || 0}|${c.last_message_text || ""}`).join("~");
  const msgsFingerprint = (msgs) =>
    (msgs || []).map((m) => `${m.wa_message_id || m.id || ""}|${m.timestamp || m.created_at || ""}|${m.is_read ? 1 : 0}`).join("~");

  const loadConversations = useCallback(async () => {
    try {
      // Feb 2026 — fast mode: primim conversațiile INSTANT (fără enrichment
      // pentru linked_orders/linked_returns). Randăm imediat, apoi
      // îmbogățim în background per peer, ca să nu blocăm UI-ul.
      const r = await axios.get(`${API}/whatsapp/conversations`, { params: { search, fast: 1 } });
      const items = r.data?.items || [];
      // 1) Aplică enrichment din cache pt UI instant (linked_orders vizibile).
      const ec = enrichCacheRef.current || {};
      const withCachedEnrichment = items.map((c) => ec[c.peer_number] ? { ...c, ...ec[c.peer_number] } : c);
      // 2) setConversations DOAR dacă amprenta e diferită (evită re-render inutil).
      setConversations((prev) => (convsFingerprint(prev) === convsFingerprint(withCachedEnrichment) ? prev : withCachedEnrichment));
      writeLS(CONVS_CACHE, items);
      // 3) Enrich în background — DOAR pt peers-ii care nu au încă în cache,
      // sau pt care last_message_at s-a schimbat (posibilă comandă nouă legată).
      const cacheByPeer = { ...ec };
      const stale = items.filter((c) => !cacheByPeer[c.peer_number]);
      if (stale.length === 0) return; // nimic nou → skip toate request-urile.
      const CONCURRENCY = 5;
      let idx = 0;
      const worker = async () => {
        while (idx < stale.length) {
          const my = idx++;
          const peer = stale[my]?.peer_number;
          if (!peer) continue;
          try {
            const lr = await axios.get(`${API}/whatsapp/conversations/${encodeURIComponent(peer)}/links`);
            const patch = { linked_orders: lr.data?.linked_orders || [], linked_returns: lr.data?.linked_returns || [] };
            cacheByPeer[peer] = patch;
            setConversations((prev) => prev.map((c) => c.peer_number === peer ? { ...c, ...patch } : c));
          } catch { /* silent — rândul rămâne fără link-uri */ }
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
      enrichCacheRef.current = cacheByPeer;
      writeLS(ENRICH_CACHE, cacheByPeer);
    } catch (e) {
      // 404/500 swallowed — page still renders empty state.
    }
  }, [search]);

  // IMPORTANT: NU pune `messages` în deps — ar crea bucla „Maximum update depth
  // exceeded" (loadMessages e citit de useEffect care apoi apelează setMessages
  // → schimbă messages → re-creează loadMessages → useEffect fires again → …).
  // Folosim functional setState + un flag captat în closure pentru mark-read.
  const loadMessages = useCallback(async (peer, { markRead = true } = {}) => {
    if (!peer) { setMessages([]); return; }
    // Mark-read optimistic + unconditional: user raport 2026-10-02 „badge-ul rămâne
    // 5-6 chiar dacă intru pe conversație și răspund". Cauza: până acum mark-read
    // fire-a doar când `didChange || !cached`, dar la conversațiile deja cache-uite
    // local și fără mesaje noi, `didChange=false` ⇒ mark-read NU se mai apela.
    // Soluție: fire mark-read UNCONDITIONAL la fiecare `loadMessages(peer)` cu
    // `markRead=true` (default). Optimistic UI: scoatem badge-ul imediat din
    // lista locală, fără să așteptăm următorul tick de 8s.
    if (markRead) {
      setConversations((prev) => prev.map((c) =>
        c.peer_number === peer && (c.unread_count || 0) > 0
          ? { ...c, unread_count: 0 }
          : c
      ));
      axios.post(`${API}/whatsapp/conversations/${encodeURIComponent(peer)}/mark-read`).catch(() => {});
    }
    // Afișează instant din cache (fără spinner) dacă avem — apoi refresh silent.
    const cached = readLS(MSGS_CACHE_PREFIX + peer);
    if (cached && cached.length) {
      setMessages(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const r = await axios.get(`${API}/whatsapp/conversations/${encodeURIComponent(peer)}/messages`);
      const items = r.data?.items || [];
      setMessages((prev) => {
        if (msgsFingerprint(prev) === msgsFingerprint(items)) return prev;
        return items;
      });
      writeLS(MSGS_CACHE_PREFIX + peer, items);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/config`);
      setConfig(r.data || {});
    } catch (e) {
      setConfig({});
    }
  }, []);

  useEffect(() => { loadConversations(); loadConfig(); }, [loadConversations, loadConfig]);
  useEffect(() => { loadMessages(activePeer); }, [activePeer, loadMessages]);

  // Auto-refresh — short cadence (8s) for "instant feel" similar to native WhatsApp.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      await loadConversations();
      if (activePeer) await loadMessages(activePeer);
    };
    const iv = setInterval(tick, 8000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeer]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Ask for browser notification permission once
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const activeConv = useMemo(
    () => conversations.find((c) => c.peer_number === activePeer),
    [conversations, activePeer]
  );

  // 24h window — if last_inbound_at > 24h ago, only templates can be sent.
  const within24hWindow = useMemo(() => {
    if (!activeConv?.last_inbound_at) return false;
    const last = new Date(activeConv.last_inbound_at).getTime();
    return Date.now() - last < 24 * 3600 * 1000;
  }, [activeConv]);

  const send = async (e) => {
    e?.preventDefault();
    if (!activePeer || !draft.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, { to: activePeer, text: draft.trim() });
      setDraft("");
      await loadMessages(activePeer);
      await loadConversations();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d?.msg).join("; ") : err?.message || "Eroare la trimitere";
      try { (await import("sonner")).toast.error(msg); } catch { window.alert(msg); }
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async (template, params) => {
    if (!activePeer || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send-template`, {
        to: activePeer,
        template_name: template.name,
        language: template.language || "ro",
        body_params: params || [],
      });
      setShowTemplates(false);
      await loadMessages(activePeer);
      await loadConversations();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d?.msg).join("; ") : err?.message || "Eroare la trimitere template";
      try { (await import("sonner")).toast.error(msg); } catch { window.alert(msg); }
    } finally {
      setSending(false);
    }
  };

  const sendFormLink = async (formType) => {
    if (!activePeer) return;
    const label = formType === "retur" ? "RETUR" : "GARANȚIE / SERVICE";
    if (!window.confirm(`Trimit clientului link-ul către formularul ${label}?`)) return;
    setSendingFormLink(true);
    try {
      await axios.post(`${API}/whatsapp/send-form-link`, {
        to: activePeer, form_type: formType,
      });
      try { (await import("sonner")).toast.success(`Link formular ${label} trimis către ${activePeer}`); } catch { window.alert(`Link formular ${label} trimis`); }
      // Refresh so the sent message appears in the thread
      const m = await axios.get(`${API}/whatsapp/conversations/${encodeURIComponent(activePeer)}/messages`);
      setMessages(m.data.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Trimitere eșuată";
      try { (await import("sonner")).toast.error(msg); } catch { window.alert(msg); }
    } finally {
      setSendingFormLink(false);
    }
  };

  const sendMedia = async (e) => {    const file = e.target.files?.[0];
    if (!file || !activePeer) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("to", activePeer);
      fd.append("file", file);
      if (draft.trim()) fd.append("caption", draft.trim());
      await axios.post(`${API}/whatsapp/send-media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft("");
      await loadMessages(activePeer);
      await loadConversations();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d?.msg).join("; ") : err?.message || "Eroare la trimitere media";
      try { (await import("sonner")).toast.error(msg); } catch { window.alert(msg); }
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isConfigured = !!config?.phone_number_id;

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-zinc-950" data-testid="whatsapp-page">
      {/* Left panel: conversations */}
      <aside className="flex w-[380px] shrink-0 flex-col border-r border-zinc-900">
        <div className="border-b border-zinc-900 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h1 className="text-sm font-semibold text-zinc-100">WhatsApp</h1>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowAutoReply(true)}
                className="rounded-md p-1.5 text-emerald-400 hover:bg-zinc-900 hover:text-emerald-300"
                title="Auto-reply în afara programului"
                data-testid="wa-open-autoreply-settings"
              >
                <Clock className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={loadConversations}
                className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                title="Reîncarcă"
                data-testid="wa-refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută conversații..."
              data-testid="wa-search"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 py-1.5 pl-7 pr-2 text-xs text-zinc-100 outline-none focus:border-emerald-500"
            />
          </div>
          {!isConfigured && (
            <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-300">
              WhatsApp neconfigurat. Mergi la <a href="/settings" className="underline">Setări → Integrări</a>.
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-500">
              {isConfigured ? "Nicio conversație. Aștept primul mesaj sau scrie tu unui număr." : "Configurează WhatsApp pentru a începe."}
            </div>
          ) : (
            conversations.map((c) => (
              <ConversationRow
                key={c.peer_number}
                conv={c}
                active={c.peer_number === activePeer}
                onClick={() => setActivePeer(c.peer_number)}
                deleting={deletingPeer === c.peer_number}
                onDelete={async (cc) => {
                  if (!window.confirm(`Ștergi TOATE mesajele conversației cu ${cc.contact_name || cc.peer_number}?\n\nAcțiunea nu poate fi anulată.`)) return;
                  setDeletingPeer(cc.peer_number);
                  try {
                    const r = await axios.delete(`${API}/whatsapp/conversations/${encodeURIComponent(cc.peer_number)}`);
                    toast.success("Conversație ștearsă", { description: `${r.data?.deleted_messages || 0} mesaje eliminate.` });
                    setConversations((prev) => prev.filter((x) => x.peer_number !== cc.peer_number));
                    if (activePeer === cc.peer_number) { setActivePeer(null); setMessages([]); }
                  } catch (err) {
                    toast.error("Nu am putut șterge", { description: err.response?.data?.detail || err.message });
                  } finally {
                    setDeletingPeer(null);
                  }
                }}
              />
            ))
          )}
        </div>
      </aside>

      {/* Right panel: thread */}
      <main className="flex min-w-0 flex-1 flex-col">
        {!activePeer ? (
          <div className="flex flex-1 items-center justify-center text-sm text-zinc-500" data-testid="wa-empty">
            Selectează o conversație din stânga.
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-zinc-900 px-4 py-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-zinc-100">
                    {activeConv?.contact_name || activePeer}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-500">{activePeer}</span>
                </div>
                {(Array.isArray(activeConv?.linked_orders) && activeConv.linked_orders.length > 0)
                 || (Array.isArray(activeConv?.linked_returns) && activeConv.linked_returns.length > 0) ? (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {(activeConv.linked_orders || []).map((o) => (
                      <a
                        key={"order-" + o.order_id + o.display_id}
                        href={`/orders?order_id=${encodeURIComponent(o.display_id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`wa-header-order-link-${o.display_id}`}
                        title={`Comandă ${o.display_id} · ${o.status_label || ""}`}
                        className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200"
                      >
                        <PlatformBadge platform={o.platform} />
                        <span>#{o.display_id}</span>
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                    {(activeConv.linked_returns || []).map((r) => {
                      const isReturn = r.type === "retur";
                      const cls = isReturn
                        ? "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20";
                      const label = isReturn ? "RETUR" : "GARANȚIE";
                      return (
                        <a
                          key={"ret-" + r.id}
                          href={`/returns/${encodeURIComponent(r.id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`wa-header-return-link-${r.number}`}
                          title={`${label} #${r.number} · ${r.status || ""}`}
                          className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] ${cls}`}
                        >
                          <span className="rounded-sm border px-1 py-[1px] text-[9px] font-bold uppercase leading-none">{label}</span>
                          <span>#{r.number}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5">
                  <button
                    data-testid="wa-send-warranty-form-btn"
                    onClick={() => sendFormLink("garantie")}
                    disabled={sendingFormLink}
                    title="Trimite clientului link către formularul de garanție/service"
                    className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold text-amber-200 hover:border-amber-500/60 hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    Trimite formular GARANȚIE
                  </button>
                  <button
                    data-testid="wa-send-return-form-btn"
                    onClick={() => sendFormLink("retur")}
                    disabled={sendingFormLink}
                    title="Trimite clientului link către formularul de retur"
                    className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] font-semibold text-rose-200 hover:border-rose-500/60 hover:bg-rose-500/20 disabled:opacity-50"
                  >
                    Trimite formular RETUR
                  </button>
                </div>
                {!within24hWindow && (
                  <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-300" title="După 24h de la ultimul mesaj client, poți trimite doar template-uri pre-aprobate Meta">
                    Fereastra 24h închisă
                  </span>
                )}
              </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto bg-[radial-gradient(circle_at_50%_0%,_rgba(16,185,129,0.04),transparent_70%)] p-4">
              {loading && messages.length === 0 && (
                <div className="text-center text-xs text-zinc-500">Se încarcă...</div>
              )}
              {messages.map((m) => (
                <MessageBubble key={m.wa_id || m.id} msg={m} />
              ))}
              <div ref={threadEndRef} />
            </div>

            <form onSubmit={send} className="border-t border-zinc-900 bg-zinc-950 p-3">
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="rounded-md border border-zinc-800 bg-zinc-900 p-2 text-zinc-400 hover:text-zinc-100 disabled:opacity-50"
                  title="Atașează fișier (PDF, imagine, video)"
                  data-testid="wa-attach-btn"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowTemplates(true)}
                  disabled={sending}
                  className="rounded-md border border-zinc-800 bg-zinc-900 p-2 text-zinc-400 hover:text-emerald-300 disabled:opacity-50"
                  title="Trimite template pre-aprobat (funcționează și după închiderea ferestrei de 24h)"
                  data-testid="wa-template-btn"
                >
                  <FileText className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={sendMedia}
                  accept="image/*,application/pdf,video/*,audio/*"
                  data-testid="wa-file-input"
                />
                <textarea
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={within24hWindow ? "Scrie un mesaj..." : "Fereastra 24h închisă — folosește butonul de template →"}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                  data-testid="wa-compose-input"
                  className="max-h-32 min-h-[40px] flex-1 resize-none rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  data-testid="wa-send-btn"
                  className="flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" />
                  {sending ? "Trimit..." : "Trimite"}
                </button>
              </div>
            </form>
          </>
        )}
      </main>

      <TemplatePicker
        open={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSend={sendTemplate}
        sending={sending}
      />
      {showAutoReply && (
        <WhatsAppAutoReplyModal onClose={() => setShowAutoReply(false)} />
      )}
    </div>
  );
}
